import { Card, CardHeader, CardContent } from '@/components/ui/Card'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { formatNumber, formatTHB } from '@/lib/utils'
import { PROCUREMENT_METHOD_LABELS, PROCUREMENT_TYPE_LABELS, PROVINCE_NAMES } from '@/lib/constants'
import type { VTenderLatestVersion } from '@/lib/pocketbase'

interface OverviewTabProps {
  version: VTenderLatestVersion
}

export function TenderOverviewTab({ version }: OverviewTabProps) {
  return (
    <div className="space-y-6">
      {/* Project details */}
      <Card>
        <CardHeader title="รายละเอียดโครงการ" />
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <DetailItem
              label="ชื่อโครงการ"
              value={version.project_name}
            />
            <DetailItem
              label="รหัสโครงการ"
              value={version.project_id}
            />
            <DetailItem
              label="วิธีการจัดซื้อจัดจ้าง"
              value={version.procurement_method ? PROCUREMENT_METHOD_LABELS[version.procurement_method] || version.procurement_method : '-'}
            />
            <DetailItem
              label="ประเภทการจัดซื้อจัดจ้าง"
              value={version.procurement_type ? PROCUREMENT_TYPE_LABELS[version.procurement_type] || version.procurement_type : '-'}
            />
            <DetailItem
              label="จังหวัด"
              value={version.province_code ? (PROVINCE_NAMES[version.province_code] || version.province_code) : '-'}
            />
            <DetailItem
              label="ปีงบประมาณ"
              value={version.budget_year ? `พ.ศ. ${version.budget_year}` : '-'}
            />
          </dl>
        </CardContent>
      </Card>

      {/* Budget information */}
      <Card>
        <CardHeader title="ข้อมูลงบประมาณ" />
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <DetailItem
              label="งบประมาณที่อนุมัติ"
              value={
                version.sum_price_agree
                  ? `${formatNumber(Math.round(version.sum_price_agree / 100))} บาท`
                  : '-'
              }
              highlight
            />
            <DetailItem
              label="ราคากลาง"
              value={version.price_build ? formatTHB(version.price_build) : '-'}
            />
          </dl>
        </CardContent>
      </Card>

      {/* Important dates */}
      <Card>
        <CardHeader title="วันสำคัญ" />
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <DetailItem
              label="วันประกาศ"
              value={version.published_at ? <DateDisplay date={version.published_at} /> : '-'}
            />
            <DetailItem
              label="วันที่มีผล"
              value={version.effective_at ? <DateDisplay date={version.effective_at} format="datetime" /> : '-'}
            />
          </dl>
        </CardContent>
      </Card>

      {/* Agency information */}
      <Card>
        <CardHeader title="ข้อมูลหน่วยงาน" />
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <DetailItem
              label="ชื่อหน่วยงาน"
              value={version.dept_name}
            />
            <DetailItem
              label="หน่วยงานย่อย"
              value={version.dept_sub_name || '-'}
            />
            <DetailItem
              label="กระทรวง"
              value={version.ministry || '-'}
            />
          </dl>
        </CardContent>
      </Card>

    </div>
  )
}

function DetailItem({
  label,
  value,
  highlight,
  mono,
}: {
  label: string
  value: React.ReactNode
  highlight?: boolean
  mono?: boolean
}) {
  return (
    <div>
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd
        className={`mt-1 ${
          highlight
            ? 'font-display text-lg font-semibold text-slate-900'
            : mono
            ? 'font-mono text-sm text-slate-700'
            : 'text-slate-900'
        }`}
      >
        {value || '-'}
      </dd>
    </div>
  )
}
